/**
 * A three-step "how to play" hint over the top of the yard for a fresh save:
 * click the egg, hatch a dinosaur, level it up. Each step is derived from
 * the state, so it needs no save field and comes back after a wipe. Gone for
 * good once a dinosaur reaches level 2 or a second species hatches.
 */

import { availableDinos } from '../data/dinos';
import type { GameState } from '../game/state';

export interface HintView {
  update(state: GameState): void;
}

function hintFor(state: GameState): string | null {
  const levels = Object.values(state.dinos).filter((n) => n > 0);
  if (levels.length >= 2 || levels.some((n) => n >= 2)) return null;
  if (state.clicks === 0 && levels.length === 0) return 'Click the egg to collect eggs.';
  if (levels.length === 0) {
    const first = availableDinos(state.eraId)[0]?.name ?? 'dinosaur';
    return `Spend eggs on a ${first} in the list on the left. It lays eggs for you.`;
  }
  return 'Level your dinosaur up to lay more. Keep an eye out for falling golden eggs.';
}

export function createHintView(host: HTMLElement): HintView {
  const box = document.createElement('div');
  box.className = 'hint';
  box.hidden = true;
  box.setAttribute('role', 'status');
  host.append(box);
  let last: string | null = '';

  return {
    update(state) {
      const text = hintFor(state);
      if (text === last) return;
      last = text;
      box.hidden = text === null;
      if (text !== null) box.textContent = text;
    },
  };
}
